import { Injectable } from '@angular/core';
import { Task } from 'src/core/master-data/domain/task';
import { TaskRepository } from 'src/core/tasks/domain/task-repository';
import { GarbageCollectorService } from '../../../garbage-collector/domain/garbage-collector.service';
import { TaskService } from '../../domain/task.service';
import { QueueService } from '../../../queue/domain/queue.service';
import { ConfirmCloseTaskRequest } from './confirm-close-task-request';
import { TimeoutException } from '../../../common/domain/exceptions/timeout-exception';
import { NoConnectionException } from '../../../common/domain/exceptions/no-connection-exception';
import { BadRequestException } from '../../../common/domain/exceptions/bad-request-exception';
import { ConfirmCloseTaskQueueElement } from '../../../queue/domain/elements/confirm-close-task-queue-element';
import { InternalServerException } from '../../../common/domain/exceptions/internal-server-exception';
import { QueueElement, QueueElementError } from '../../../queue/domain/queue-element';
import { MasterDataService } from '../../../master-data/domain/master-data-service';

@Injectable({
  providedIn: 'any',
})
export class ConfirmCloseTask {
  private isSavedToServer = false;
  private isConfirmClosedToServer = false;
  private isRetry = false;
  private task: Task;

  constructor(
    private readonly taskRepository: TaskRepository,
    private readonly garbageCollector: GarbageCollectorService,
    private readonly taskService: TaskService,
    private readonly queueService: QueueService,
    private readonly masterDataService: MasterDataService
  ) {}

  async execute(request: ConfirmCloseTaskRequest, bulkMode = false): Promise<void> {
    this.task = request.task;
    const queueElement = request.queueElement;
    this.isRetry = queueElement !== null;
    this.isSavedToServer = queueElement !== null ? queueElement.operationStatus.isSavedToServer : false;
    this.isConfirmClosedToServer = queueElement !== null ? queueElement.operationStatus.isConfirmClosedToServer : false;

    try {
      if (!this.isRetry) {
        this.task.confirmClose();
        this.markTaskAsFinalDependingOnResolution();
        await this.saveTaskIntoLocalDevice();
      }

      const taskIsInQueue = this.queueService.hasQueueElementWithTaskId(this.task.id);

      if (bulkMode) {
        await this.enqueueOperation();
        return;
      }

      if (taskIsInQueue && !this.isRetry) {
        await this.enqueueOperation();
        return;
      }

      await this.saveTaskToExternalServer(this.task);
      await this.confirmCloseTaskToExternalServer(this.task);
      await this.garbageCollector.addDirectoryToDelete(this.taskService.getAttachmentsDirectoryForTaskId(this.task.id));
    } catch (exception) {
      if (this.isConnectionProblem(exception)) {
        await this.enqueueOperation();
        return;
      }

      // exceptions during retrying do not enqueue or rollback the task, but store error to display in list
      if (this.isRetry) {
        // store the errors in the enqueued element
        if (exception instanceof BadRequestException) {
          await this.updateQueueElementWithBadRequestException(exception, queueElement);
        }
        if (exception instanceof InternalServerException) {
          await this.updateQueueElementWithInternalServerException(queueElement);
        }
        throw exception; // propagate exception without doing anything else
      }

      // otherwise, rollback
      this.task.rollback();
      await this.saveTaskIntoLocalDevice();
      throw exception; // propagate exception
    }
  }

  private async updateQueueElementWithBadRequestException(
    exception: BadRequestException,
    queueElement: ConfirmCloseTaskQueueElement
  ) {
    const errors = exception.getErrors();
    await this.updateQueueElementWithErrors(queueElement, errors);
  }

  private async updateQueueElementWithInternalServerException(queueElement: ConfirmCloseTaskQueueElement) {
    const errors = [QueueElement.getInternalServerExceptionError()];
    await this.updateQueueElementWithErrors(queueElement, errors);
  }

  private async updateQueueElementWithErrors(
    queueElement: ConfirmCloseTaskQueueElement,
    errors: Array<QueueElementError>
  ) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isConfirmClosedToServer: this.isConfirmClosedToServer,
    };
    queueElement.update(this.task, operationStatus, errors);
    await this.queueService.updateQueueElement(queueElement);
  }

  private async enqueueOperation(errors: Array<QueueElementError> = []) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isConfirmClosedToServer: this.isConfirmClosedToServer,
    };
    await this.queueService.enqueue(ConfirmCloseTaskQueueElement.fromTask(this.task, operationStatus, errors));
  }

  private isConnectionProblem(exception: any): boolean {
    return exception instanceof TimeoutException || exception instanceof NoConnectionException;
  }

  private async saveTaskIntoLocalDevice() {
    await this.taskService.saveTask(this.task);
  }

  private async saveTaskToExternalServer(task: Task) {
    if (this.isSavedToServer) {
      return;
    }
    await this.taskRepository.save(task);
    this.isSavedToServer = true;
  }

  private async confirmCloseTaskToExternalServer(task: Task) {
    if (this.isConfirmClosedToServer) {
      return;
    }
    await this.taskRepository.confirmClose(task);
    this.isConfirmClosedToServer = true;
  }

  private markTaskAsFinalDependingOnResolution(): void {
    const taskResolutionId = this.task.getConfirmCloseTaskData().resolution;
    if (!taskResolutionId) {
      return;
    }

    if (!this.masterDataService.getConfirmCloseTaskActions().isResolutionFinal(taskResolutionId)) {
      return;
    }

    this.task.markAsFinalState();
  }
}
